import { DesignPoint } from '@/ship/Models/Design';
import { pick } from 'lodash';
import { between } from '@/utils/Helper';
import { RemarkRejectionEnum } from '@/ship/Enums/DesignEnums';

import DesigningTools from '@/ship/Enums/DesigningTools';

export interface DrawingOptions {
    points: DesignPoint[];
    text?: string;
    selected?: boolean;
    status?: RemarkRejectionEnum;
    scaleIndex?: number;
    isReact?: boolean;
    color?: string;
    designingTool?: DesigningTools;
}

export interface DrawingRectangleOptions extends DrawingOptions {
    drawPin?: boolean;
    color?: string;
}

/**
 * brightColor - color of area
 * stringColor - color of moving pin
 */
export const palette = {
    brightColor: 'rgba(38, 200, 124, 0.3)',
    strongColor: '#30C07C',
    redColor: '#DC3545',
    darkFontColor: '#484848',
    lightFontColor: '#FFFFFF',
};
// Default pin size (values increased in 2.5 - default image scaling)
export const pinHeight: number = 115;
export const pinWidth: number = 100;
export const pinTextSize: number = 30;
export const pinTextOffset: number = 20;
export const pinHeightOffset: number = 20;

export class CanvasDrawer {
    ctx!: CanvasRenderingContext2D;

    pinImage: HTMLImageElement = new Image();
    pinSelectedImage: HTMLImageElement = new Image();
    pinCompletedImage: HTMLImageElement = new Image();
    pinRejectedImage: HTMLImageElement = new Image();
    backgroundImage: HTMLImageElement = new Image();

    constructor(ctx: CanvasRenderingContext2D) {
        this.ctx = ctx;

        // Static images
        this.pinImage.src = require(`@/assets/pin-default.svg?inline`);
        this.pinSelectedImage.src = require(`@/assets/pin-selected.svg?inline`);
        this.pinCompletedImage.src = require(`@/assets/pin-completed.svg?inline`);
        this.pinRejectedImage.src = require(`@/assets/pin-rejected.svg?inline`);
    }

    public setBackground(image: HTMLImageElement) {
        this.backgroundImage = image;
    }

    public drawFigure(options: DrawingOptions & { designingTool?: DesigningTools }) {
        const { points = [], designingTool, color } = options;
        const tool = designingTool;

        if (points.length === 1) {
            return this.drawPin(options);
        }

        if (points.length === 2) {
            switch (tool) {
                case DesigningTools.Ellipse:
                    return this.drawEllipse({ ...options, drawPin: false });
                case DesigningTools.Marker:
                    return this.drawMarkerPen(options, 'marker');
                case DesigningTools.Pen:
                    return this.drawMarkerPen(options, 'pen');
                case DesigningTools.Arrow:
                    return this.drawArrow(options);
                default:
                    return this.drawRectangle({ ...options, drawPin: true, color });
            }
        }

        if (points.length > 2) {
            switch (tool) {
                case DesigningTools.Marker:
                    return this.drawMarkerPen(options, 'marker');
                case DesigningTools.Pen:
                    return this.drawMarkerPen(options, 'pen');
                default:
                    return;
            }
        }
    }

    public drawBackground(scaleIndex: number) {
        this.ctx.drawImage(
            this.backgroundImage,
            0,
            0,
            this.backgroundImage.width * scaleIndex,
            this.backgroundImage.height * scaleIndex,
        );
    }

    public drawRectangle(args: DrawingRectangleOptions) {
        const { points, scaleIndex = 1, color } = args;
        if (args.points.length !== 2) return;

        this.ctx.beginPath();

        this.ctx.fillStyle = color ? this.hexToRgba(color) : palette.brightColor;
        this.ctx.strokeStyle = color || palette.strongColor;

        const { width, height } = this.findRectangleParams(points);
        const [startPoint] = points;

        const params = [startPoint.x, startPoint.y, width, height].map((parameter) => parameter * scaleIndex) as [
            number,
            number,
            number,
            number,
        ];

        this.ctx.fillRect(...params);
        this.ctx.strokeRect(...params);

        this.ctx.fill();
        this.ctx.stroke();
        this.ctx.closePath();

        if (args.drawPin) {
            const point = { x: startPoint.x + width / 2, y: startPoint.y + height / 2 };
            const pinOptions = {
                points: [point],
                ...pick(args, ['text', 'selected', 'completed', 'scaleIndex', 'status']),
                isReact: true,
            };
            this.drawPin(pinOptions);
        }
    }

    private hexToRgba(hex: string, alpha: number = 0.3): string {
        if (!hex.startsWith('#')) return hex;

        hex = hex.substring(1);
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    public drawPin(args: DrawingOptions) {
        const { points, selected, status, text, scaleIndex = 1, isReact = false } = args;
        if (points.length !== 1) return;

        const [{ x, y }] = points;

        this.ctx.beginPath();

        let pin = this.pinImage;
        switch (status) {
            case RemarkRejectionEnum.Completed:
                pin = this.pinCompletedImage;
                break;
            case RemarkRejectionEnum.Rejected:
                pin = this.pinRejectedImage;
                break;
            case RemarkRejectionEnum.None:
                this.pinImage.src = '';
                break;
        }

        this.ctx.globalAlpha = 0.5;

        this.ctx.drawImage(
            selected
                ? this.pinSelectedImage
                : isReact && status === RemarkRejectionEnum.Created
                ? this.pinCompletedImage
                : pin,
            (x - pinWidth / 2) * scaleIndex,
            (y - pinHeight + pinHeightOffset) * scaleIndex,
            pinWidth * scaleIndex,
            pinHeight * scaleIndex,
        );

        const offsetTextY = y - (pinHeight - pinTextOffset) / 2;
        this.drawText(
            x,
            offsetTextY,
            text,
            selected || status !== RemarkRejectionEnum.Created || (status === RemarkRejectionEnum.Created && isReact),
            scaleIndex,
        );

        this.ctx.globalAlpha = 1;

        this.ctx.closePath();
    }

    public drawText(x: number, y: number, text: string = '', light: boolean = false, scaleIndex: number = 1) {
        if (!text) return;

        this.ctx.beginPath();

        this.ctx.fillStyle = light ? palette.lightFontColor : palette.darkFontColor;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.font = `${pinTextSize * scaleIndex}px Roboto`;
        this.ctx.fillText(text, x * scaleIndex, y * scaleIndex);
        this.ctx.fill();

        this.ctx.closePath();
    }

    public drawEllipse(options: DrawingRectangleOptions & { selected?: boolean }) {
        const { points, scaleIndex = 1, selected = false, color } = options;
        if (points.length !== 2) return;

        this.ctx.beginPath();

        this.ctx.fillStyle = 'transparent';
        this.ctx.strokeStyle = selected ? '#4682B4' : color || palette.redColor;
        this.ctx.lineWidth = selected ? 2 : 2;

        const [start, end] = points;
        const width = Math.abs(end.x - start.x) * scaleIndex;
        const height = Math.abs(end.y - start.y) * scaleIndex;
        const centerX = Math.min(start.x, end.x) * scaleIndex + width / 2;
        const centerY = Math.min(start.y, end.y) * scaleIndex + height / 2;

        this.ctx.ellipse(centerX, centerY, width / 2, height / 2, 0, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.stroke();
        this.ctx.closePath();
    }
    public drawMarkerPen(options: DrawingOptions & { selected?: boolean }, designingTool: 'marker' | 'pen') {
        if (options.points.length < 2) return;

        const { scaleIndex = 1, selected = false } = options;

        let strokeColor;
        if (designingTool === 'marker') {
            strokeColor = selected ? 'rgba(70, 130, 180, 0.5' : 'rgba(220, 53, 69, 0.5)';
        } else {
            strokeColor = selected ? '#4682B4' : options.color || palette.redColor;
        }

        const lineWidth =
            designingTool === 'marker' ? (selected ? 10 : 10) * scaleIndex : (selected ? 5 : 5) * scaleIndex;

        this.ctx.beginPath();
        this.ctx.moveTo(options.points[0].x * scaleIndex, options.points[0].y * scaleIndex);

        for (let i = 1; i < options.points.length; i++) {
            this.ctx.lineTo(options.points[i].x * scaleIndex, options.points[i].y * scaleIndex);
        }

        this.ctx.strokeStyle = strokeColor;
        this.ctx.lineWidth = lineWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.stroke();
        this.ctx.closePath();
    }

    public pointInFreeform(points: DesignPoint[], x: number, y: number): boolean {
        if (points.length < 2) return false;

        const lineWidth = 10;

        for (let i = 0; i < points.length - 1; i++) {
            const start = points[i];
            const end = points[i + 1];
            const distanceToSegment = this.checkLine(start, end, { x, y });
            if (distanceToSegment <= lineWidth) {
                return true;
            }
        }

        return false;
    }

    public drawArrow(options: DrawingOptions & { selected?: boolean }) {
        if (options.points.length !== 2) return;

        const scaleIndex = options.scaleIndex || 1;
        const [start, end] = options.points.map((point) => ({
            x: point.x * scaleIndex,
            y: point.y * scaleIndex,
        }));

        const strokeColor = options.selected ? '#4682B4' : options.color || palette.redColor;
        const lineWidth = options.selected ? 2 : 2;

        this.ctx.beginPath();
        this.ctx.moveTo(start.x, start.y);
        this.ctx.lineTo(end.x, end.y);

        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLength = 15 * scaleIndex;

        this.ctx.moveTo(end.x, end.y);
        this.ctx.lineTo(
            end.x - headLength * Math.cos(angle - Math.PI / 6),
            end.y - headLength * Math.sin(angle - Math.PI / 6),
        );

        this.ctx.moveTo(end.x, end.y);
        this.ctx.lineTo(
            end.x - headLength * Math.cos(angle + Math.PI / 6),
            end.y - headLength * Math.sin(angle + Math.PI / 6),
        );

        this.ctx.strokeStyle = strokeColor;
        this.ctx.lineWidth = lineWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.stroke();
        this.ctx.closePath();
    }

    public pointInArrow(points: DesignPoint[], x: number, y: number): boolean {
        if (points.length !== 2) return false;

        const [start, end] = points;
        const lineWidth = 10;

        const checkLine = this.checkLine(start, end, { x, y });
        return checkLine <= lineWidth;
    }

    private checkLine(start: DesignPoint, end: DesignPoint, point: DesignPoint): number {
        const xLine = end.x - start.x;
        const yLine = end.y - start.y;

        const xPoint = point.x - start.x;
        const yPoint = point.y - start.y;

        const lineLengthSq = xLine * xLine + yLine * yLine;
        if (lineLengthSq === 0) {
            return Math.sqrt(xPoint * xPoint + yPoint * yPoint);
        }

        const t = (xPoint * xLine + yPoint * yLine) / lineLengthSq;
        const clampedT = Math.max(0, Math.min(1, t));

        const closestX = start.x + clampedT * xLine;
        const closestY = start.y + clampedT * yLine;

        const x = point.x - closestX;
        const y = point.y - closestY;
        return Math.sqrt(x * x + y * y);
    }

    public findRectangleParams(points: DesignPoint[]) {
        const [start, end] = points;
        const height = Math.abs(end.y - start.y);
        const width = Math.abs(end.x - start.x);

        return { height, width };
    }

    public pointInRectangle(points: DesignPoint[], x: number, y: number): boolean {
        const [start, end] = points;
        if (points.length !== 2 || !start || !end) return false;

        const { width, height } = this.findRectangleParams(points);
        const pin = [{ x: start.x + width / 2, y: start.y + height / 2 }];

        return (between(x, start.x, end.x) && between(y, start.y, end.y)) || this.pointInPin(pin, x, y);
    }

    public pointInEllipse(points: DesignPoint[], x: number, y: number): boolean {
        const [start, end] = points;
        if (points.length !== 2 || !start || !end) return false;

        const width = Math.abs(end.x - start.x);
        const height = Math.abs(end.y - start.y);
        const centerX = Math.min(start.x, end.x) + width / 2;
        const centerY = Math.min(start.y, end.y) + height / 2;

        const normalizedX = (x - centerX) / (width / 2);
        const normalizedY = (y - centerY) / (height / 2);
        const isInsideEllipse = normalizedX ** 2 + normalizedY ** 2 <= 1;

        const pin = [{ x: centerX, y: centerY }];
        return isInsideEllipse || this.pointInPin(pin, x, y);
    }

    public pointInPin(points: DesignPoint[], x: number, y: number): boolean {
        const [point] = points;
        if (!point) return false;

        return (
            between(x, point.x - pinWidth / 2, point.x + pinWidth / 2) &&
            between(y, point.y - pinHeight + pinHeightOffset, point.y)
        );
    }
}

export default CanvasDrawer;
